import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { DateTime, Interval } from 'luxon';
import { useReactToPrint } from 'react-to-print';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { confirmDialog } from 'primereact/confirmdialog';
import { InputTextarea } from 'primereact/inputtextarea';
import UtilContext from '../UtilContext';
import AuthContext from '../AuthContext';
import { LocaleContext } from '../LocaleContext';
import { displayCrmStepName } from '../../utils/crmStateI18n';
import { getPusherChannel } from '../../utils/pusherChannel';
import { userIdsEqual } from '../../utils/serviceTaskExecutors';


export default function ServiceTaskPlanner({
  serviceTaskId,
  taskTableId,
  getRequest,      // (url) => Promise<{data: any}>
  postRequest,     // (url, body, isFormData=false) => Promise<{data: any}>
  employees = [],  // [{id, full_name}]
  canManage,
  onTableChange,     // (nextTable) => void
  onSummaryChange,   // ({ planned, forecast, final, done, delayDays, rawDelay }) => void
  taskTheme,
  taskDescription,
  taskSenderId,
  isCrmTaskTable = false,
  crmSteps = [],
  crmTicketType = null,
}) {

  const [state,dispatch] = useContext(DatabaseContext);
  const { setDialogText, setVisible, toast, getDateFormat } = useContext(UtilContext);
  const {user}= useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';

  const statusOptions = useMemo(
    () => [
      { id: '', name: '' },
      { id: 'Rozpracovaná', name: t('ServiceTaskPlanner.Status.InProgress') },
      { id: 'Hotová', name: t('ServiceTaskPlanner.Status.Done') },
    ],
    [t]
  );

  const [table, setTable] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tplDialog, setTplDialog] = useState(false);
  const [tplName, setTplName] = useState('');
  const [employeeOpts, setEmployeeOpts] = useState(employees);
  const crmStepOptions = useMemo(() => {
    const typeAliases = crmTicketType === 'quote_request'
      ? new Set(['quote_request', 'inquiry'])
      : crmTicketType === 'acquisition'
        ? new Set(['acquisition'])
        : null;
    return (Array.isArray(crmSteps) ? crmSteps : [])
      .filter((step) => step?.active !== false)
      .filter((step) => {
        if (!typeAliases) return true;
        const types = Array.isArray(step?.applicable_types) ? step.applicable_types : [];
        if (types.length === 0) return true;
        return types.some((type) => typeAliases.has(String(type)));
      })
      .map((step) => ({ id: step.id, name: displayCrmStepName(step?.name, locale) }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', locale || 'sk'));
  }, [crmSteps, crmTicketType, locale]);

  const [editRowId, setEditRowId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [editScope, setEditScope] = useState(null); // 'full' | 'limited' | null
  const [editingStart, setEditingStart] = useState(false);
  const [startDraft, setStartDraft] = useState(null);

  const plannerRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => plannerRef.current,
    documentTitle: t('ServiceTaskPlanner.PrintDocumentTitle'),
    pageStyle: `
      @page { size: A4 landscape; margin: 15mm; }
      body { font-family: sans-serif; font-size: 12px; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #ccc; padding: 4px; text-align: left; }
      .no-print { display: none !important; }
    `
  });

  const notify = (severity, summary, detail) =>
    toast?.current?.show({ severity, summary, detail, life: 2200 });

  // load table + rows
  const fetchTable = async () => {
    const res = await getRequest(`/api/task_tables/${taskTableId}`);
    const tbl = res?.data ?? res;
    const sortedRows = (tbl.rows || []).slice().sort(
      (a, b) => (a.order_index || 0) - (b.order_index || 0)
    );
    setTable(tbl);
    setRows(sortedRows);
    return { ...tbl, rows: sortedRows };
  };


  useEffect(() => {
    if (!taskTableId) return;
    const opts = [{ id: '', name: '—' }].concat(
        (employees || []).filter(user => user?.employee && (user?.employee?.date_of_departure=="" || user?.employee?.date_of_departure==null)).map(user => ({ id: user.id, name: user?.employee?.name ?? user?.username }))
    );
    setEmployeeOpts(opts);
    fetchTable().catch(() => notify('error', t('Common.Error'), t('ServiceTaskPlanner.TableLoadFailed')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskTableId, state?.service_tasks, t]);

  useEffect(() => {
    if (!taskTableId || !serviceTaskId) return;
    const channel = getPusherChannel();
    if (!channel) return;

    const eventName = isCrmTaskTable ? 'crm_communication_ticket.updated' : 'service_task.updated';
    const onOwnerUpdated = (payload) => {
      const ownerId = isCrmTaskTable
        ? (payload?.ticket_id ?? payload?.id ?? payload?.ticket?.id)
        : (payload?.task_id ?? payload?.id ?? payload?.task?.id);
      if (ownerId == null || Number(ownerId) !== Number(serviceTaskId)) return;
      fetchTable()
        .then((updatedTable) => {
          if (typeof onTableChange === 'function') onTableChange(updatedTable);
        })
        .catch(() => {});
    };

    channel.bind(eventName, onOwnerUpdated);
    return () => {
      channel.unbind(eventName, onOwnerUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCrmTaskTable, serviceTaskId, taskTableId]);

  useEffect(() => {
    console.log(table,canManage);
    setStartDraft(table?.start_date ? DateTime.fromISO(table.start_date).toJSDate() : null);
  }, [table]);

  useEffect(() => {
    if (typeof onSummaryChange !== 'function') return;

    const planned   = computePlannedCompletion(rows, table?.start_date);
    const finalDone = computeFinalCompletion(rows);
    const forecast  = computeForecastCompletion(rows, table?.start_date);
    const done      = !!finalDone;
    const shownDate = done ? finalDone : forecast;
    const rawDelay  = (planned && shownDate) ? businessDaysDiff(planned, shownDate) : 0;
    const delayDays = Math.max(0, rawDelay);

    onSummaryChange({
      planned,
      forecast,
      final: finalDone,
      done,
      delayDays,
      rawDelay,
      plannedStr: planned ? planned.toFormat(dateFmt) : null,
      forecastStr: forecast ? forecast.toFormat(dateFmt) : null,
      finalStr: finalDone ? finalDone.toFormat(dateFmt) : null,
    });
  // stringify rows kvôli porovnaniu obsahu bez hlbokej rovnosti
  }, [JSON.stringify(rows), table?.start_date]);

  const addRow = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('task_table_id', taskTableId);
      fd.append('order_index', (rows?.length || 0) + 1);
      fd.append('title', t('ServiceTaskPlanner.DefaultNewRowTitle'));
      fd.append('duration_days', 0);
      fd.append('chain_mode', 'sequential');

      const res = await postRequest('/api/task_table_rows', fd, true);
      const created = res?.data ?? res;
      setRows(prev => [...prev, created]);

      notify('success', t('ServiceTaskPlanner.RowAdded'), t('ServiceTaskPlanner.RowAddedDetail'));

      // server po add prepočíta expected_date (žlté) cez recalc
      const updatedTable = await fetchTable();

      // informuj rodiča (ak bol callback zadaný)
      if (typeof onTableChange === 'function') onTableChange(updatedTable);
    } catch {
      notify('error', t('Common.Error'), t('ServiceTaskPlanner.RowAddFailed'));
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (rowId) => {
    try {
      setDialogText(t('ServiceTaskPlanner.DeletingRow'));
      setVisible(true);
      const fd = new FormData();
      fd.append('_method', 'DELETE');
      await postRequest(`/api/task_table_rows/${rowId}`, fd, true);

      const r = await getRequest(`/api/task_tables/${taskTableId}`);
      const p = r?.data ?? r;
      const tableData = p?.data ?? p;
      setRows(tableData?.rows || []);
      setTable(tableData);

      notify('success', t('ServiceTaskPlanner.RowDeleted'), t('ServiceTaskPlanner.RowDeletedDetail'));

      if (typeof onTableChange === 'function') onTableChange(tableData);
    } catch (e) {
      notify('error', t('Common.Error'), t('ServiceTaskPlanner.RowDeleteFailed'));
    }
    finally{
      setVisible(false);
    }
  };

    // drag & drop reorder
  const onRowReorder = async (e) => {
    if (!canManage) return;

    const from = e.dragIndex;
    const to   = e.dropIndex;

    // vypočítaj boundary z pôvodných dát (posledný locked)
    const boundary = lockBoundaryIndex(rows);

    // blokuj presun locked riadku alebo do/z hlavičky
    if (isLocked(rows[from])) {
      notify('warn', t('ServiceTaskPlanner.NotAllowed'), t('ServiceTaskPlanner.ReorderDoneNoMove'));
      return;
    }
    // povoliť len v taili (ZA boundary)
    if (from <= boundary || to <= boundary) {
      notify('warn', t('ServiceTaskPlanner.NotAllowed'), t('ServiceTaskPlanner.ReorderAboveDoneNoMove'));
      return;
    }

    // e.value je nové poradie od PrimeReact – ale pre istotu nechaj head (0..boundary) presne ako bol
    const next = e.value;
    const head = rows.slice(0, boundary + 1); // pôvodná hlavička (locked)
    const tail = next.slice(boundary + 1);    // nová časť za boundary z e.value

    // lokálne optimisticky: zlož späť head + tail a prečísluj order_index
    let reordered = [...head, ...tail].map((r, i) => ({ ...r, order_index: i + 1 }));

    // vynúť prvý vizuálne sekvenčný (UI)
    if (reordered[0] && reordered[0].chain_mode !== 'sequential') {
      reordered[0] = { ...reordered[0], chain_mode: 'sequential' };
    }
    setRows(reordered);

    try {
      setSaving(true);

      // ⬇️ kľúčové: posielaj IBA TAIL payload (riadky ZA boundary)
      const tailPayload = reordered
        .slice(boundary + 1)
        .map(r => ({
          id: r.id,
          order_index: r.order_index,
          chain_mode: r.chain_mode, // nech máš aj UI sync
        }));

      const fd = new FormData();
      fd.append('rows', JSON.stringify(tailPayload)); // ⇐ TAIL

      const res = await postRequest(`/api/task_tables/${taskTableId}/reorder`, fd, true);
      if (res?.message) {
        alert(res.message);
        return;
      }

      const payload = res?.data ?? res;
      setTable(payload);
      setRows(payload?.rows ?? reordered);
      if (typeof onTableChange === 'function') onTableChange(payload);

      notify('success', t('ServiceTaskPlanner.OrderSaved'), t('ServiceTaskPlanner.OrderSavedDetail'));
    } catch {
      notify('error', t('Common.Error'), t('ServiceTaskPlanner.OrderSaveFailed'));
      // fallback – reload
      const fresh = await getRequest(`/api/task_tables/${taskTableId}`);
      const tableData = fresh?.data ?? fresh;
      setTable(tableData);
      setRows(tableData?.rows ?? rows);
    } finally {
      setSaving(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) return;
    setSaving(true);
    setDialogText(t('Common.Saving'));
    setVisible(true);
    try {
      const fd = new FormData();
      fd.append('name', tplName);
      await postRequest(`/api/task_tables/${taskTableId}/saveAsTemplate`, fd, true);
      getRequest("/api/task_templates").then((response) => {
        dispatch({type:'load_task_templates',newTaskTemplates:response.data});
      });
      setTplDialog(false);
      notify('success', t('ServiceTaskPlanner.OrderSaved'), t('ServiceTaskPlanner.TemplateCreated'));
    } catch {
      notify('error', t('Common.Error'), t('ServiceTaskPlanner.TemplateCreateFailed'));
    } finally {
      setSaving(false);
      setVisible(false);
    }
  };

  const saveStartDate = async () => {
    if (!startDraft) return;

    // posledná poistka zostáva
    const progressed = rows?.some(r => !!r?.performed_date || r?.status === 'Rozpracovaná' || r?.status === 'Hotová');
    if (progressed) {
      notify('warn', t('ServiceTaskPlanner.StartRejectedAfterProgress'), t('ServiceTaskPlanner.StartRejectedAfterProgressDetail'));
      setEditingStart(false);
      return;
    }

    try {
      const fd = new FormData();
      // kľúčová zmena:
      const isoDate = DateTime.fromJSDate(startDraft).toISODate(); // "YYYY-MM-DD" bez časovej zóny
      fd.append('start_date', isoDate);

      await postRequest(`/api/task_tables/update/${taskTableId}`, fd, true);
      notify('success', t('ServiceTaskPlanner.OrderSaved'), t('ServiceTaskPlanner.StartSet'));
      await fetchTable();
    } catch {
      notify('error', t('Common.Error'), t('ServiceTaskPlanner.StartSaveFailed'));
    } finally {
      setEditingStart(false);
    }
  };

    const canEditAsAssignee = (row) =>
      !canManage &&
      user?.id &&
      userIdsEqual(row.assignee_id, user.id) &&
      row.status !== 'Hotová';

    // Polia stavu/vykonania: až keď je nastavený začiatok (inak sú prázdne deadline)
    const EXECUTION_FIELDS = ['assignee_id', 'performed_date', 'status', 'note'];

    // jediný zdroj pravdy: či smie užívateľ editovať konkrétne pole
    const canEditField = (row, field) => {
      if (!editRowId || editRowId !== row.id) return false;
      if (!table?.start_date && EXECUTION_FIELDS.includes(field)) return false;
      if (editScope === 'full') return true;
      if (editScope === 'limited') {
        return ['performed_date','status','note'].includes(field);
      }
      return false;
    };

    const canDeleteRow = (row) =>
      !table?.start_date ||
      (!row.performed_date && (row.status === '' || row.status == null));

    const actionButtons = (row) => {
      if (!canSeeActions(row)) return null;

      const isEditing = editRowId === row.id;
      const assignee = isAssignee(row);
      const done = isDoneStatus(row);

      // assignee po "Hotová" už neupravuje
      const canStartEdit = canManage || (assignee && !done);

      if (!isEditing) {
        return (
          <div className="flex gap-2">
            {canStartEdit && (
              <Button
                icon="pi pi-pencil"
                className="p-button-sm p-button-text"
                onClick={() => startEdit(row)}  // nastaví full/limited
              />
            )}
            {canManage && (
              <Button
                icon="pi pi-trash"
                className="p-button-sm p-button-text p-button-danger"
                disabled={!canDeleteRow(row)}
                onClick={() => {
                  if (!canDeleteRow(row)) return;
                  confirmDialog({
                    message: t('ServiceTaskPlanner.ConfirmDeleteRow'),
                    header: t('ServiceTaskPlanner.ConfirmHeader'),
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: t('Common.Yes'),
                    rejectLabel: t('Common.No'),
                    accept: () => deleteRow(row.id),
                  });
                }}
                tooltip={canDeleteRow(row) ? t('ServiceTaskPlanner.DeleteRowTooltip') : t('ServiceTaskPlanner.DeleteRowDisabledTooltip')}
                tooltipOptions={{ position: 'top' }}
              />
            )}
          </div>
        );
      }

      // počas editovania – pre obe roly rovnaké ovládanie (u saveEdit riešiš patch na backend)
      return (
        <div className="flex flex-col gap-2">
          <Button
            icon="pi pi-check"
            className="p-button-sm p-button-text p-button-success"
            onClick={saveEdit}
          />
          <Button
            icon="pi pi-times"
            className="p-button-sm p-button-text p-button-danger"
            onClick={cancelEdit}
          />
        </div>
      );
    };

    // bezpečne vráti názov z options podľa id
    const getLabelById = (options, id) => {
      const sId = id === null || id === undefined ? '' : String(id);
      const match = (options || []).find(o => String(o.id) === sId);
      return match ? (match.name || match.label || '') : '';
    };

    const TextCellMultiline = (row, field, placeholder='') => {
      const data = getDraftedRow(row);
      const editable = canEditField(row, field);
      return editable ? (
        <InputTextarea
          autoResize rows={2}
          value={data[field] || ''}
          onChange={(e)=>patchDraft({ [field]: e.target.value || '' })}
          placeholder={placeholder}
          className="w-56 p-inputtext-sm"
        />
      ) : (
        <div className="block whitespace-pre-wrap break-words leading-snug" title={data[field] || ''}>
          {data[field] || ''}
        </div>
      );
    };

    const DropdownCell = (row, field, options, placeholder = '—', allRows = []) => {
      const data = getDraftedRow(row);
      const editable = canEditField(row, field);
      const raw = data[field];
      const currentId =
        typeof raw === 'object' && raw !== null
          ? raw.id ?? raw.value ?? ''
          : raw ?? '';

      const handleChange = (e) => {
        const nextId = e.value ?? '';
        const chosen = (options || []).find((o) => String(o.id) === String(nextId));
        const isHotova = chosen && chosen.id === 'Hotová';

        if (isHotova && !data.performed_date) {
          const todayIso = DateTime.now().toISODate();
          patchDraft({ [field]: nextId, performed_date: todayIso });
        } else {
          patchDraft({ [field]: nextId });
        }
      };

      const label = getLabelById(options, currentId);
      // 🧠 zoradíme podľa order_index pred výpočtom
      const sortedRows = [...allRows].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
      // nájdi prvý, ktorý NIE je hotový
      const firstUnfinishedIndex = sortedRows.findIndex(
        (r) => r?.status !== 'Hotová'
      );
      const isFirstUnfinished =
        firstUnfinishedIndex !== -1 && sortedRows[firstUnfinishedIndex]?.id === row.id;

      // 🟢🟡🔴 logika bodky podľa stavu a deadline
      const renderStatusDot = (value) => {
        const normalized = (value || '').trim();
        const isHotova = normalized === 'Hotová';
        const isRozpracovana = normalized === 'Rozpracovaná';

        const today = DateTime.now().startOf('day');
        const deadline = row?.expected_date
          ? DateTime.fromISO(row.expected_date).startOf('day')
          : null;

        let color = '';
        // 🔵 1) Hotová = zelená
        if (isHotova) {
          color = 'bg-green-500';
        }
        // 🟡 2) Rozpracovaná = žltá alebo červená (ak mešká)
        else if (isRozpracovana) {
          color = (deadline && deadline < today) ? 'bg-red-500' : 'bg-yellow-400';
        }
        // 🔴 3) Prázdny stav — zobraz len AK je to prvý nehotový a deadline je v minulosti
        else if (!normalized && isFirstUnfinished && deadline && deadline < today) {
          color = 'bg-red-500';
        }
        // ⚪ 4) Inak nič
        if (!color) return null;
        return (
          <span className={`inline-block w-4 h-4 rounded-full mr-2 ${color}`} />
        );
      };

      // 🧩 len pre zobrazenie – nie v editovacom režime
      if (!editable) {
        return (
          <span className="flex items-center max-w-[10rem] truncate" title={label}>
            {renderStatusDot(row.status)}
            <span>{label}</span>
          </span>
        );
      }

      // 🧩 editor bez bodiek
      return (
        <Dropdown
          value={currentId}
          options={options}
          optionLabel="name"
          optionValue="id"
          onChange={(e) => handleChange(e)}
          placeholder={placeholder}
          showClear
          filter
          className="w-36 p-inputtext-sm"
          panelClassName="max-w-[80vw]"
        />
      );
    };

    const ParallelCell = (row, opts) => {
      const data = getDraftedRow(row);
      const isFirst = (typeof opts?.rowIndex === 'number' && opts.rowIndex === 0) || data?.order_index === 1;
      const editable = canEditField(row, 'chain_mode') && !isFirst;
      const isParallel = !isFirst && data.chain_mode === 'parallel_prev';

      return editable ? (
        <div className="flex items-center justify-center">
          <Checkbox
            inputId={`par-${row.id}`}
            checked={isParallel}
            onChange={(e)=>patchDraft({ chain_mode: e.checked ? 'parallel_prev' : 'sequential' })}
          />
        </div>
      ) : (
        isFirst
          ? <span className="text-center block text-gray-400">—</span>
          : <span className="text-center block">{isParallel ? '||' : '→'}</span>
      );
    };

    const DurationCell = (row) => {
      const data = getDraftedRow(row);
      const editable = canEditField(row, 'duration_days');
      return editable ? (
        <InputNumber
          value={Number.isFinite(+data.duration_days) ? +data.duration_days : 0}
          onValueChange={(e)=>patchDraft({ duration_days: e.value ?? 0 })}
          showButtons min={0} inputClassName="w-20 text-center p-inputtext-sm" className="p-inputnumber-sm" suffix={` ${t('ServiceTaskPlanner.DaysShort')}`}
        />
      ) : (
        <span>{(data.duration_days || 0) + ` ${t('ServiceTaskPlanner.DaysShort')}`}</span>
      );
    };

    const DelayBadge = ({ days }) => {
      // 0 alebo nič = nezobrazuj
      if (days == null || days === 0) return null;

      const isEarly = days < 0;
      const label = isEarly ? days : `+${days}`; // napr. -2 alebo +3
      const colorClass = isEarly ? 'text-emerald-600' : 'text-red-600';

      return (
        <span className={`ml-1 font-semibold ${colorClass}`}>
          {label}
        </span>
      );
    };


    const PerformedCell = (row) => {
      const data = getDraftedRow(row);
      const editable = canEditField(row, 'performed_date');

      // vypočítaj meškanie v pracovných dňoch (len ak máme expected aj performed)
      const expectedDay = data?.expected_date ? DateTime.fromISO(data.expected_date).startOf('day') : null;
      const performedDay = data?.performed_date ? DateTime.fromISO(data.performed_date).startOf('day') : null;
      const delayDays = (expectedDay && performedDay) ? businessDaysDiff(expectedDay, performedDay) : 0;

      if (editable) {
        return (
          <div className="flex items-center">
            <Calendar
              value={data.performed_date ? DateTime.fromISO(data.performed_date).toJSDate() : null}
              onChange={(e) => {
                const newDate = e.value ? DateTime.fromJSDate(e.value).toISODate() : '';
                const newStatus = newDate ? 'Hotová' : (data.status === 'Hotová' ? 'Rozpracovaná' : data.status);
                patchDraft({
                  performed_date: newDate,
                  status: newStatus,
                });
              }}
              dateFormat="dd.mm.yy"
              showIcon
              placeholder="—"
              inputClassName="p-inputtext-sm w-28"
              className="w-36"
            />
            <DelayBadge days={delayDays} />
          </div>
        );
      }

      return (
        <span className="inline-flex items-center">
          {data.performed_date ? DateTime.fromISO(data.performed_date).toFormat(dateFmt) : ''}
          <DelayBadge days={delayDays} />
        </span>
      );
    };

    const NoteCell = (row) => {
      const data = getDraftedRow(row);
      const editable = canEditField(row, 'note');
      return editable ? (
        <InputTextarea
          autoResize rows={2}
          value={data?.note || ''}
          onChange={(e)=>patchDraft({ note: e.target.value || '' })}
          placeholder={t('ServiceTaskPlanner.NotePlaceholder')} className="w-40 p-inputtext-sm py-2"
        />
      ) : (
        <div className="block whitespace-pre-wrap break-words leading-snug" title={data?.note || ''}>
          {data?.note || ''}
        </div>
      );
    };

    const isWeekend = (dt) => dt.weekday === 6 || dt.weekday === 7;

    /** addBusinessDays(DateTime|ISO, n>=0) -> DateTime (start sa ráta ako deň 0) */
    function addBusinessDays(start, days) {
      let d = typeof start === 'string'
        ? DateTime.fromISO(start).startOf('day')
        : start.startOf('day');

      let left = Math.max(0, Math.floor(Number(days) || 0));
      while (left > 0) {
        d = d.plus({ days: 1 });
        if (!isWeekend(d)) left--;
      }
      return d;
    }

    // INKLUZÍVNY koniec: 0/1 => start; >=2 => start + (n-1) pracovných dní
    function endForDurationInclusive(startISO, days) {
      const n = Math.max(0, Math.floor(Number(days) || 0));
      const start = DateTime.fromISO(startISO).startOf('day');
      if (n <= 1) return start;
      return addBusinessDays(startISO, n - 1);
    }

    const businessDaysDiff = (a, b) => {
      if (!a || !b) return 0;
      if (a.equals(b)) return 0;
      let from = a < b ? a : b;
      let to   = a < b ? b : a;
      let count = 0;
      let cur = from;
      while (cur < to) {
        cur = cur.plus({ days: 1 });
        if (!isWeekend(cur)) count++;
      }
      return a < b ? count : -count;
    };

    const toDay = (iso) => (iso ? DateTime.fromISO(iso).startOf('day') : null);

    const getPerformedDay = (r) =>
      toDay(r?.performed_date ?? r?.performed_at ?? r?.done_at ?? r?.completed_at);
    
    const getExpectedDay = (r) =>
      toDay(r?.expected_date ?? r?.deadline ?? r?.expected_at);

    // pokúsi sa nájsť počet plánovaných dní v riadku (podporuje viac políčok)
    const getPlannedDays = (row) => {
      const val =
        row.plan_days ??
        row.planned_days ??
        row.plannedDays ??
        row.duration_days ??
        row.duration ??
        0;
      const n = Number(val);
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
    };

    const computeMaxPerformed = (rows) => {
      const list = rows.map(getPerformedDay).filter(d => d && d.toMillis);
      if (!list.length) return null;
      return list.reduce((m, d) => (d.toMillis() > m.toMillis() ? d : m), list[0]);
    };

    const computeMaxExpected = (rows) => {
      const list = rows.map(getExpectedDay).filter(d => d && d.toMillis);
      if (!list.length) return null;
      return list.reduce((m, d) => (d.toMillis() > m.toMillis() ? d : m), list[0]);
    };

    const computeAllDone = (rows) => rows?.length > 0 && rows.every(r => !!getPerformedDay(r));
    // ---------- core výpočty ----------
    /**
     * Plánované ukončenie: start_date + súčet plánovaných pracovných dní (víkendy preskočiť).
     * Pozn.: ak chceš „prvý deň sa počíta ako 0“, nechaj takto; ak sa má rátať už deň začiatku, uber 1.
     */
    // (voliteľne nechaj svoj pôvodný business-day výpočet ako fallback)
    function computePlannedCompletion(rows, startISO) {
      const start = toDay(startISO);
      if (!start || !Array.isArray(rows) || rows.length === 0) return null;

      let chainAnchorExpected = start;             // kotva na začiatku bloku
      let blockMaxExpected    = start;             // priebežné max deadline v bloku

      const sorted = [...rows].sort((a,b) => (a.order_index||0)-(b.order_index||0));
      sorted.forEach((row, idx) => {
        // začiatok nového bloku = prvý riadok alebo explicitne 'sequential'
        if (idx === 0 || row.chain_mode === 'sequential') {
          blockMaxExpected = chainAnchorExpected;
        }
        const dur = Math.max(0, Math.floor(Number(row.duration_days) || 0));
        // deadline = kotva + 'dur' pracovných dní (inklu: 0/1 => kotva; inak posúvame)
        // s business dňami nemusíme odpočítavať 1 napevno – posun spraví addBusinessDays.
        const expected = addBusinessDays(chainAnchorExpected, dur);
        if (expected > blockMaxExpected) blockMaxExpected = expected;
        // koniec bloku: ak ďalší je sekvenčný alebo už nie je ďalší
        const next = sorted[idx + 1];
        const blockEndsHere = !next || next.chain_mode === 'sequential';
        if (blockEndsHere) {
          chainAnchorExpected = blockMaxExpected;
        }
      });
      return chainAnchorExpected; // DateTime
    }

    /**
     * Finálne ukončenie: max z reálnych „performed_at“ (ak chceš iný kľúč, uprav).
     */
    const computeFinalCompletion = (rows) => {
      if (!computeAllDone(rows)) return null;
      return computeMaxPerformed(rows); // všetko hotové -> posledné vykonanie
    };

    /**
     * Predpoveď: vypočítaj sekvenčne plánované „koncové dni“ pre každý riadok,
     * porovnaj s reálnym vykonaním (ak existuje) a POSÚVY sčítaj.
     * Posun je signovaný (môže byť aj záporný = rýchlejšie).
     */
    const computeForecastCompletion = (rows, startISO = null) => {
      const maxExpected  = computeMaxExpected(rows);
      const maxPerformed = computeMaxPerformed(rows);

      if (maxExpected || maxPerformed) {
        if (maxExpected && maxPerformed) {
          return maxPerformed.toMillis() > maxExpected.toMillis() ? maxPerformed : maxExpected;
        }
        return maxExpected || maxPerformed;
      }

      // Fallback, ak nie sú expected/performed – môžeš nechať svoje pôvodné počítanie
      const start = toDay(startISO);
      if (!start) return null;

      // minimálny fallback: plán = start + súčet trvaní (prac. dni)
      const total = rows.reduce((acc, r) => {
        const n = Math.max(0, Math.floor(Number(r?.duration_days ?? r?.duration ?? 0) || 0));
        return acc + n;
      }, 0);

      // jednoduché business-day sčítanie (0/1 -> start, inak + (total-1))
      const isWeekend = (dt) => dt.weekday === 6 || dt.weekday === 7;
      const addBusinessDays = (s, days) => {
        let d = s;
        if (!days) return d;
        let left = days;
        while (left > 0) {
          d = d.plus({ days: 1 });
          if (!isWeekend(d)) left--;
        }
        return d;
      };
    const forecast = total <= 1 ? start : addBusinessDays(start, total - 1);
      return forecast;
    };

    // helper – status hotová (bez ohľadu na diakritiku/písmo)
    const isDoneStatus = (row) => (row?.status || '').toString() == 'Hotová';
    const isAssignee = (row) => userIdsEqual(user?.id, row?.assignee_id);
    const canSeeActions = (row) => canManage || isAssignee(row);

    // zámok riadku
    const isLocked = (row) => !!row.performed_date || isDoneStatus(row);

    // index posledného zamknutého riadku
    const lockBoundaryIndex = (rows) => {
      let last = -1;
      rows.forEach((r, i) => { if (isLocked(r)) last = i; });
      return last;
    };

    const DragHandle = (rowData) => {
      return isLocked(rowData)
        ? <i className="pi pi-lock text-gray-400" title={t('ServiceTaskPlanner.DragDoneNoMove')} />
        : <i className="pi pi-bars text-gray-500" title={t('ServiceTaskPlanner.DragMove')} />;
    };

    // pri vstupe do edit módu
    const startEdit = (row) => {
      if (!canSeeActions(row)) return;
      if (isAssignee(row) && !canManage) {
        if (isDoneStatus(row)) return; // assignee po "Hotová" už nie
        setEditScope('limited');
      } else {
        setEditScope('full');
      }
      setEditRowId(row.id);
      setEditDraft({ ...row }); // snapshot riadku
    };
    // zrušiť edit
    const cancelEdit = () => {
      setEditRowId(null);
      setEditDraft(null);
      setEditScope(null);
    };

    const ALLOWED_FIELDS_ALL = ['title','assignee_id','duration_days','chain_mode','performed_date','status','note','order_index', ...(isCrmTaskTable ? ['crm_step_id'] : [])];
    const ALLOWED_FIELDS_ASSIGNEE = ['performed_date','status','note'];

    const filterAllowed = (row, diff) => {
      const keys = canManage ? ALLOWED_FIELDS_ALL : (canEditAsAssignee(row) ? ALLOWED_FIELDS_ASSIGNEE : []);
      return Object.fromEntries(Object.entries(diff).filter(([k]) => keys.includes(k)));
    };

    // nahrad tvoje saveEdit touto verziou
  const saveEdit = async () => {
    if (!editRowId || !editDraft) return;

    const original = rows.find(r => r.id === editRowId);
    if (!original) { setEditRowId(null); setEditDraft(null); return; }

    // priprav diff len z toho, čo sa zmenilo
    const draftNormalized = {
      title: editDraft.title ?? original.title ?? '',
      assignee_id: editDraft.assignee_id ?? original.assignee_id ?? null,
      crm_step_id: editDraft.crm_step_id ?? original.crm_step_id ?? null,
      duration_days: Number.isFinite(+editDraft.duration_days) ? +editDraft.duration_days : (original.duration_days ?? 0),
      chain_mode: editDraft.chain_mode === 'parallel_prev' ? 'parallel_prev'
                : editDraft.chain_mode === 'sequential' ? 'sequential'
                : (original.chain_mode ?? 'sequential'),
      performed_date: editDraft.performed_date ?? original.performed_date ?? null,
      status: editDraft.status ?? original.status ?? '',
      note: editDraft.note ?? original.note ?? '',
      order_index: editDraft.order_index ?? original.order_index, // obvykle sa tu nemení
    };

    const diff = Object.fromEntries(
      Object.entries(draftNormalized).filter(([k, v]) => {
        // porovnaj s pôvodným; normalizuj null/'' pre nullable polia
        const orig = original[k];
        const norm = (x) => (x === '' ? null : x);
        return norm(v) !== norm(orig);
      })
    );

    // povoľ len polia, na ktoré má user právo
    const payload = filterAllowed(original, diff);

    // ak nie je čo ukladať (alebo user nemá právo), skonči
    if (Object.keys(payload).length === 0) {
      setEditRowId(null);
      setEditDraft(null);
      return;
    }

    try {
      setSaving(true);
      setDialogText(t('Common.Saving'));
      setVisible(true);

      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        // nechaj prejsť prázdny string — Laravel ho cez ConvertEmptyStringsToNull spraví na null (pri nullable)
        if (v === undefined) return;
        fd.append(k, v);
      });

      const res = await postRequest(`/api/task_table_rows/update/${editRowId}`, fd, true);

      // backend vracia celú tabuľku (podľa tvojej updateRow), tak si ju dotiahni
      const fresh = await getRequest(`/api/task_tables/${taskTableId}`);
      const payloadTable = fresh?.data ?? fresh;
      setTable(payloadTable);
      setRows(payloadTable?.rows ?? []);

      if (typeof onTableChange === 'function') onTableChange(payloadTable);

      notify('success', t('ServiceTaskPlanner.RowSaved'), t('ServiceTaskPlanner.RowSavedDetail'));
    } catch (e) {
      notify('error', t('Common.Error'), t('ServiceTaskPlanner.RowSaveFailed'));
    } finally {
      setVisible(false);
      setSaving(false);
      setEditRowId(null);
      setEditDraft(null);
    }
  };

    // ak je tento row v edit móde, používaj editDraft, inak row
    const useRowData = (row) => (editRowId === row.id && editDraft ? editDraft : row);
    const getDraftedRow = (row) => (
      editRowId === row.id ? { ...row, ...(editDraft || {}) } : row
    );
    // univerzálne nastavenie draftu
    const patchDraft = (patch) => setEditDraft(prev => ({ ...prev, ...patch }));

    const summary = useMemo(() => {
      const planned    = computePlannedCompletion(rows, table?.start_date); // bez vykonal
      const allDone    = computeAllDone(rows);
      const finalDone  = allDone ? computeMaxPerformed(rows) : null;
      const forecast   = computeForecastCompletion(rows, table?.start_date);
      const shownDate  = allDone ? finalDone : forecast;

      const rawDelay   = (planned && shownDate) ? businessDaysDiff(planned, shownDate) : 0;
      const delayDays  = Math.max(0, rawDelay);

      return { planned, shownDate, allDone, delayDays, rawDelay };
    }, [JSON.stringify(rows), table?.start_date]);

    const hasProgress = Array.isArray(rows) && rows.some(r =>
      !!r?.performed_date ||
      r?.status === 'Rozpracovaná' ||
      r?.status === 'Hotová'
    );

  return (
    <div className="space-y-3">
      {/* Header nad tabuľkou */}
      <div>
        <h2 className="text-xl font-semibold">{table?.title || t('ServiceTaskPlanner.HeadingFallback')}</h2>
        {table?.description ? (
          <p className="text-sm text-gray-600">{table.description}</p>
        ) : null}
        {table ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">{t('ServiceTaskPlanner.StartLabel')}</span>
            {!editingStart ? (
                <>
                <span className="font-medium">{table?.start_date ? DateTime.fromISO(table.start_date).toFormat(dateFmt): t('ServiceTaskPlanner.StartNotSet')}</span>
                <Button label={t('ServiceTaskPlanner.SetStart')} size="small" className="p-button-text" disabled={hasProgress}                       // ⬅️ blokuj tlačidlo
                  onClick={() => {
                    if (hasProgress) {
                      notify('warn', t('ServiceTaskPlanner.StartRejectedAfterProgress'), t('ServiceTaskPlanner.StartBlockedFillingDetail'));
                      return;
                    }
                    setEditingStart(true);
                  }} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }}/>
                </>
            ) : (
                <>
                <Calendar value={startDraft}
                            onChange={(e)=> setStartDraft(e.value)}
                            dateFormat="dd.mm.yy" showIcon className='w-40' />
                <Button label={t('Common.Save')} size="small" onClick={saveStartDate} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }}/>
                <Button label={t('Common.Cancel')} size="small" severity="secondary" className="p-button-text" style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }}
                        onClick={()=>{
                          setEditingStart(false);
                          setStartDraft(table?.start_date ? DateTime.fromISO(table.start_date).toJSDate() : null);
                        }} />
                </>
            )}
            {table && !table?.start_date && (
              <div className="mt-1 space-y-0.5">
                <p className="text-xs text-amber-700">{t('ServiceTaskPlanner.NeedStartHint')}</p>
                {rows?.length > 0 && (
                  <p className="text-xs text-blue-700">{t('ServiceTaskPlanner.RecalcAfterStartHint')}</p>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Toolbar */}
      <Toolbar
        className="!py-2 !px-3"
        left={
          <div className="flex gap-2">
            <Button label={t('ServiceTaskPlanner.AddRow')} icon="pi pi-plus" onClick={addRow} loading={saving} disabled={!canManage} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }} />
            {/*<Button label={t('Ui.R67.Zoradit_podla_Vykonal')} icon="pi pi-sort-amount-up" onClick={sortByPerformed} loading={saving} />*/}
          </div>
        }
        right={
          <div className="flex gap-2">
            <Button label={t('ServiceTaskPlanner.SaveAsTemplate')} disabled={!canManage} icon="pi pi-save" severity="secondary" onClick={() => setTplDialog(true)} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }}/>
            <Button
              label={t('ServiceTaskPlanner.Print')}
              icon="pi pi-print"
              onClick={handlePrint}
              severity="secondary" style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }}
            />
          </div>
        }
      />

      {/* Tabuľka úloh (viditeľná v náhľade, hore pod názvom) */}
        <div className="w-full min-w-0 border rounded-xl overflow-x-auto" ref={plannerRef}>
            <div className="hidden print:block mb-4 mx-2">
              <div className=" mb-2">
                <label className="font-semibold text-lg">{t('ServiceTaskPlanner.PrintLabelTheme')}</label>
                <div className="block whitespace-pre-wrap break-words text-base">
                  {taskTheme ?? state?.service_tasks?.find(task => task.id == serviceTaskId)?.theme}
                </div>
              </div>

              <div className="mb-2">
                <label className="font-semibold text-lg">{t('ServiceTaskPlanner.PrintLabelDescription')}</label>
                <div className="block whitespace-pre-wrap break-words text-base"
                  dangerouslySetInnerHTML={{
                    __html: state?.service_tasks?.find(task => task.id == serviceTaskId)?.description || ''
                  }}
                />
              </div>

              <div className="mb-2">
                <label className="font-semibold text-lg">{t('ServiceTaskPlanner.PrintLabelTicketAuthor')}</label>
                <div className="block whitespace-pre-wrap break-words text-base">
                  {state.employees.filter(
                    employee => employee.id == (taskSenderId ?? state?.service_tasks?.find(task => task.id == serviceTaskId)?.sender_id)
                  )[0]?.name}
                </div>
              </div>
            </div>
          <DataTable
            value={rows}
            dataKey="id"
            sortMode="single"
            sortField="order_index"
            sortOrder={1}
            onSort={(e) => e.preventDefault()}
            reorderableRows={canManage}
            onRowReorder={canManage ? onRowReorder : undefined}
            size="small"
            stripedRows
            scrollable
            scrollDirection="horizontal"
            style={{ width: '100%' }}
            tableStyle={{ width: '100%' }}
            className="text-sm"
            rowClassName={(row) => (editRowId === row.id ? 'bg-blue-50' : '')}
            emptyMessage={t('ServiceTaskPlanner.EmptyMessage')}
          >
            {canManage && (
              <Column header="" body={DragHandle} rowReorder style={{ width: '2.5rem', textAlign: 'center' }}
                      className="no-print" headerClassName="no-print" footerClassName="no-print"/>
            )}
            <Column header="#" body={(r, opts) => opts.rowIndex + 1} style={{ width: '3.5rem', whiteSpace: 'nowrap' }} />

            <Column header={t('ServiceTaskPlanner.TaskNamePlaceholder')}
                    body={(row) => TextCellMultiline(row, 'title', t('ServiceTaskPlanner.TaskNamePlaceholder'))}
                    style={{ width: '220px' }} />

            {isCrmTaskTable ? (
              <Column
                header={t('ServiceTaskPlanner.CrmStepColumn')}
                body={(row) => DropdownCell(row, 'crm_step_id', crmStepOptions, '—')}
                style={{ width: '190px' }}
              />
            ) : null}

            <Column header={t('ServiceTaskPlanner.AssigneeColumn')}
                    body={(row) => DropdownCell(row, 'assignee_id', employeeOpts, t('ServiceTaskPlanner.AssigneePlaceholder'))}
                    style={{ width: '170px' }} />

            <Column header={t('ServiceTaskPlanner.Duration')} body={DurationCell} style={{ width: '110px', textAlign: 'center' }} />

            <Column header={t('ServiceTaskPlanner.Chaining')} body={(row, opts) => ParallelCell(row, opts)} style={{ width: '90px', textAlign: 'center' }} />

            <Column header={t('ServiceTaskPlanner.Deadline')}
                    body={(row) => <span>{row?.expected_date ? DateTime.fromISO(row.expected_date).toFormat(dateFmt) : ''}</span>}
                    style={{ width: '120px' }}/>

            <Column header={t('ServiceTaskPlanner.Performed')} body={PerformedCell} style={{ width: '160px' }} />

            <Column header={t('ServiceTaskPlanner.StatusColumn')} body={(row) => DropdownCell(row, 'status', statusOptions, '—',rows)} style={{ width: '150px' }} />

            <Column header={t('ServiceTaskPlanner.NoteColumn')} body={NoteCell} style={{ width: '180px' }} />

            <Column
              header={t('ServiceTaskPlanner.Actions')}
              body={actionButtons}
              style={{ width: '100px' }}
              className="no-print"
              headerClassName="no-print"
              footerClassName="no-print"
            />
          </DataTable>
        </div>
        <div className="mb-2">
          <div className="mt-3 p-3 border rounded-lg bg-gray-50 flex items-center justify-between">
            <div className="text-sm">
              <div className="font-medium">
                {summary?.allDone ? t('ServiceTaskPlanner.CompletedOn') : t('ServiceTaskPlanner.ForecastCompletion')}
              </div>
              <div className="text-lg">
                {summary?.shownDate ? summary.shownDate.toFormat(dateFmt) : '—'}
              </div>

              {summary?.planned && (
                <div className="text-xs text-gray-600 mt-1">
                  {t('ServiceTaskPlanner.PlanLabel')} {summary.planned.toFormat(dateFmt)}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {summary?.allDone ? (
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 text-xs">{t('ServiceTaskPlanner.DoneBadge')}</span>
              ) : (
                <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs">{t('ServiceTaskPlanner.InProgressBadge')}</span>
              )}

              {summary?.planned && summary?.shownDate && summary?.delayDays > 0 && (
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 text-xs">
                  {summary.allDone ? t('ServiceTaskPlanner.DelayWas') : t('ServiceTaskPlanner.DelayPrefix')}+{summary.delayDays} {t('ServiceTaskPlanner.DaysShort')}
                </span>
              )}

              {summary?.planned && summary?.shownDate && summary?.rawDelay < 0 && (
                <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 text-xs">
                  {t('ServiceTaskPlanner.AheadBy')} {Math.abs(summary.rawDelay)} {t('ServiceTaskPlanner.DaysShort')}
                </span>
              )}
            </div>
          </div>
        </div>

      {/* Dialog – uložiť ako šablónu */}
      <Dialog header={t('ServiceTaskPlanner.TemplateDialogTitle')} visible={tplDialog} style={{ width: '30rem' }} onHide={() => setTplDialog(false)}>
        <div className="flex flex-col gap-3">
          <span className="p-float-label">
            <InputText id="tplname" value={tplName} onChange={(e) => setTplName(e.target.value)} className="w-full" />
            <label htmlFor="tplname">{t('ServiceTaskPlanner.TemplateNameLabel')}</label>
          </span>
          <div className="flex justify-end gap-2">
            <Button label={t('Common.Cancel')} severity="secondary" onClick={() => setTplDialog(false)} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }} />
            <Button label={t('Common.Save')} icon="pi pi-check" onClick={saveAsTemplate} loading={saving} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }}/>
          </div>
        </div>
      </Dialog>
    </div>
  );
}